import { LinkOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { Modal, message } from "antd";
import React, { useState } from "react";

// Tutorials shown to parents
const GUIDES = [
  {
    _id: "guide-parent-fee",
    question: "Fee",
    answer: "View fee details, payment history and receipts.",
    steps: [
      "Log into the Is-Kool parent account and open the menu.",
      "Under Finance, select Fee.",
      "The fee page shows the student details and the fee summary with the total, paid and remaining amounts.",
      "Click View History to open the transaction history. It lists each payment with the fee type, paid date, amount, payment type and due date.",
      "Use the receipt icon to download the receipt.",
      "Close the history to see the term-wise fee split with the discount, paid amount and remaining amount.",
    ],
    video: "/assets/help-videos/parent-fee.mp4",
  },
  {
    _id: "guide-parent-inquiry",
    question: "Admission Inquiry",
    answer: "Send an admission inquiry to the school.",
    steps: [
      "Log into the Is-Kool parent account and open the menu.",
      "Under School, select Admissions.",
      "Open the Inquiry tab. It lists the inquiries you have sent. Click Add Inquiry.",
      "Enter the child's first name, last name, date of birth, gender and the class the child is interested in.",
      "Enter the parent name and phone number (required). You can also add the email, address and notes.",
    ],
    video: "/assets/help-videos/parent-inquiry.mp4",
  },
  {
    _id: "guide-parent-report-card",
    question: "Report Card",
    answer: "View, download and acknowledge your child's report card.",
    steps: [
      "Log into the Is-Kool parent account and open the menu.",
      "Under School, select Report.",
      "Select the child from the name drop-down. The report cards are listed with the template, status and date.",
      "Click View to open a report card, or Download to save it.",
      "The report card shows the overall grade, total marks and overall comment, plus the marks, grade and comment for each subject.",
      "Tick the checkbox to acknowledge the report card.",
    ],
    video: "/assets/help-videos/parent-report-card.mp4",
  },
  {
    _id: "guide-parent-homework",
    question: "Homework",
    answer: "View the homework sent by your child's instructors.",
    steps: [
      "Log into the Is-Kool parent account and open the menu.",
      "Under Academics, select Homework.",
      "Select the child from the student name drop-down.",
      "Choose All Subjects or pick a particular subject. The red badge shows the number of new messages.",
      "The class broadcast shows the homework messages from the instructors with the date, time and attached files.",
    ],
    video: "/assets/help-videos/parent-homework.mp4",
  },
  {
    _id: "guide-parent-course-board",
    question: "Course Board",
    answer: "View the syllabus and modules for each subject.",
    steps: [
      "Log into the Is-Kool parent account and open the menu.",
      "Under School, select Course Board.",
      "Select the child from the name drop-down.",
      "The subjects are listed with the instructor for each subject. Click View for the subject you want.",
      "The subject page has two tabs, Syllabus and Module. The Module tab shows the units, such as Unit 1 and Unit 2.",
      "The Syllabus tab lists the units with the type, start date and due date. Click Download to save the syllabus.",
    ],
    video: "/assets/help-videos/parent-course-board.mp4",
  },
  {
    _id: "guide-parent-message",
    question: "Messages",
    answer: "Send a message to your child's instructor.",
    steps: [
      "Log into the Is-Kool parent account and open the menu.",
      "Scroll down and, under Communication, select Message.",
      "Click Create New Message.",
      "Select the student from the drop-down.",
      "Select the instructor you want to message.",
      "Type your message in the box and click Send. A success message appears and your message is shown in the chat.",
    ],
    video: "/assets/help-videos/parent-message.mp4",
  },
  {
    _id: "guide-parent-certificate-request",
    question: "Certificate Request",
    answer: "Request a certificate for your child from the school.",
    steps: [
      "Log into the Is-Kool parent account and open the menu.",
      "Scroll down and select Certificate Request.",
      "The page lists your earlier certificate requests. Click Request Certificate.",
      "In the pop-up, select the student from the drop-down.",
      "Select the certificate type and enter the reason for requesting it.",
      "Click Submit Request.",
    ],
    video: "/assets/help-videos/parent-certificate-request.mp4",
  },
  {
    _id: "guide-parent-settings",
    question: "Settings",
    answer: "Change your username or password.",
    steps: [
      "Log into the Is-Kool parent account and click Settings on the left side.",
      "On the change settings page, the Username option lets you edit your username. Click Update to save it.",
      "To change the password, select the Password option.",
      "Enter the old password, then the new password, and confirm the new password.",
      "Click Update.",
    ],
    video: "/assets/help-videos/parent-settings.mp4",
  },
];

export default function HelpPage() {
  const [playingId, setPlayingId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const playing = GUIDES.find((g) => g._id === playingId);

  const toggleMore = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const copyLink = (video) => {
    navigator.clipboard
      .writeText(window.location.origin + video)
      .then(() => message.success("Video link copied"))
      .catch(() => message.error("Could not copy link"));
  };

  return (
    <div className="parent-help p-4 sm:p-8">
      <style>{`
        .parent-help .yt-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, 130px);
          gap: 14px 10px;
        }
        .parent-help .yt-card { cursor: pointer; }
        .parent-help .yt-thumb {
          position: relative;
          aspect-ratio: 16 / 9;
          border-radius: 6px;
          overflow: hidden;
          background: #063C4E;
        }
        .parent-help .yt-thumb video {
          width: 100%;
          height: 100%;
          object-fit: cover;
          pointer-events: none;
          transition: transform .25s;
        }
        .parent-help .yt-card:hover .yt-thumb video { transform: scale(1.04); }
        .parent-help .yt-thumb-play {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 18px;
          background: rgba(0, 0, 0, .18);
          opacity: 0;
          transition: opacity .2s;
        }
        .parent-help .yt-card:hover .yt-thumb-play { opacity: 1; }
        .parent-help .yt-title {
          font-size: 11px;
          font-weight: 600;
          color: #111827;
          line-height: 1.35;
        }
        .parent-help .yt-desc {
          font-size: 10px;
          color: #6b7280;
          line-height: 1.45;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .parent-help .yt-more {
          font-size: 10px;
          font-weight: 500;
          color: #09617E;
          background: none;
          border: 0;
          padding: 0;
          cursor: pointer;
        }
        .parent-help .yt-more:hover { text-decoration: underline; }
        .parent-help .yt-steps {
          margin: 4px 0 0;
          padding-left: 16px;
          font-size: 10px;
          color: #4b5563;
          line-height: 1.5;
        }
        .parent-help .yt-steps li + li { margin-top: 2px; }
        .parent-help .yt-player {
          width: 100%;
          aspect-ratio: 16 / 9;
          display: block;
          background: #000;
          border-radius: 12px;
        }
        @media (max-width: 480px) {
          .parent-help .yt-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 14px 10px;
            justify-content: stretch;
          }
        }
      `}</style>
      <div className="bg-white p-4 sm:p-6 rounded-2xl space-y-6">
        <div className="yt-grid">
          {GUIDES.map((guide) => (
            <div
              key={guide._id}
              className="yt-card space-y-2"
              onClick={() => setPlayingId(guide._id)}
            >
              <div className="yt-thumb">
                <video muted preload="metadata" src={`${guide.video}#t=1`} />
                <div className="yt-thumb-play">
                  <PlayCircleOutlined />
                </div>
              </div>
              <div className="min-w-0">
                <div className="yt-title">{guide.question}</div>
                <div className="yt-desc">{guide.answer}</div>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    type="button"
                    className="yt-more"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleMore(guide._id);
                    }}
                  >
                    {expanded[guide._id] ? "Show less" : "View more"}
                  </button>
                  <button
                    type="button"
                    className="yt-more"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyLink(guide.video);
                    }}
                  >
                    <LinkOutlined /> Copy link
                  </button>
                </div>
                {expanded[guide._id] && (
                  <ol className="yt-steps" onClick={(e) => e.stopPropagation()}>
                    {guide.steps.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-xl text-orange-500 text-medium">
              Contact Info
            </div>
            <div className="text-gray-500 ">Phone: +91 95913 45370</div>
            <div className="text-gray-500 ">
              Email Id: contactus@twinbotz.com
            </div>
          </div>
        </div>
      </div>

      <Modal
        width={900}
        open={!!playing}
        onCancel={() => setPlayingId(null)}
        footer={null}
        destroyOnClose
      >
        {playing && (
          <div className="space-y-3 pt-6">
            <video
              controls
              preload="metadata"
              className="yt-player"
              src={playing.video}
            />
            <div className="yt-title">{playing.question}</div>
            <div className="text-gray-600">{playing.answer}</div>
          </div>
        )}
      </Modal>
    </div>
  );
}
